"use client";
import { useAtom } from "jotai";
import "react-responsive-modal/styles.css";
import { useMatchRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  useAuth,
  useI18N,
  useSearchPageParams,
  useNotifications,
  useSavedSearches,
  useAccessibility,
  useSearchFilterSections,
} from "common/hooks";
import { useHandleSavedSearchSelect } from "@/hooks/useHandleSavedSearchSelect";
import { searchPageSections } from "@/lib/accessibilityFilterState";
import { useAccessibilitySelectionCount } from "@/hooks/useAccessibilitySelectionCount";
import { drawerMenuOpenAtom } from "@/store/atoms";
import HamburgerMenu from "../profile/HamburgerMenu";
import { BookOpenTextIcon } from "@phosphor-icons/react";
import { LearningHubModal, useOpenLearningHub } from "@/features/learning-hub";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewAtom,
  venueDetailsOpenedAtom,
  viewModeAtom,
  searchOverlayOpenAtom,
  mapCenterAtom,
  searchTermInputAtom,
} from "@/state/atoms";
import { bucketUrlPrefix, defaultRegion, enableMapperSectionsDesktop, showLoginByDefault } from "common/config";
import { resetAllAppState } from "common/utils";
import { actionToPerformAfterLoginAtom } from "@/store/atoms";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";

import { SlidersHorizontalIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { trackEvent } from "common/analytics";
import { entryPageFromCurrentPage } from "@/analytics/entryPage";
import { SearchBar } from "@/components/Search/SearchBar";
import { NotificationPanel } from "@/components/Notifications/NotificationPanel";
import { useSearchDisplayTerm } from "@/hooks/useSearchDisplayTerm";
import { decodeSelection } from "common/utils/accessibilitySelection";
import {BellIcon, HeartStraightIcon} from "@phosphor-icons/react";

// Routes where the search box should be hidden
// Add route paths here to hide the search box on those routes
const ROUTES_WITHOUT_SEARCH = [
  "/", // home page
  "/accessibility-guide/$venueId", // Accessibility guide venue page
  "/accessibility-guide-space/$venueId/$spaceId", // Accessibility guide space page
  "/whats-nearby/$venueId", // Area map page
] as const;

function Navbar() {
  const i18n = useI18N();
  const { isLoggedIn, user, isMapper } = useAuth();
  const shouldUseMapperView = isMapper && enableMapperSectionsDesktop;
  const navigate = useNavigate();
  const { setSelectedVenue } = useSelectedVenue();
  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [, setViewMode] = useAtom(viewModeAtom);
  const { showModal } = useAuthModals();

  // Global search params for syncing with BrandsSection navigation
  const { searchPageParams, setSearchPageParams } = useSearchPageParams();

  // Prefetch accessibility data so the filter panel is always populated on every route
  const { filterAccessibility } = useAccessibility();

  // Derive search term from global state using custom hook
  const derivedSearchTerm = useSearchDisplayTerm();

    // Internal state for search term (used in header/overlay layouts)
    const [internalSearchTerm, setInternalSearchTerm] = useAtom(searchTermInputAtom);

  // Business types and opening hours; accessibility needs the panel's sections, resolved further down.
  const otherFilterCount = useMemo(() => {
    let count = 0;
    // Count business types (pipe-separated string)
    if (searchPageParams.types && searchPageParams.types.trim()) {
      count += searchPageParams.types.split("|").filter((t) => t.trim()).length;
    }
    // Count opening hours filter (only when not"any")
    if (
      searchPageParams.openingHoursMode &&
      searchPageParams.openingHoursMode !== "any"
    ) {
      count += 1;
    }
    return count;
  }, [
    searchPageParams.types,
    searchPageParams.openingHoursMode,
  ]);

  // Parse filter values from searchPageParams for overlay SearchBar
  const parsedAccessibilityOptions = useMemo(
    () =>
      decodeSelection(
        searchPageParams.accessibilities,
        searchPageParams.accessibility_sections,
      ),
    [searchPageParams.accessibilities, searchPageParams.accessibility_sections],
  );

  const parsedBusinessTypes = useMemo(() => {
    if (!searchPageParams.types) return [];
    return searchPageParams.types.split("|").filter((t) => t.trim());
  }, [searchPageParams.types]);

  // Parse opening hours values from searchPageParams for overlay SearchBar
  const parsedOpeningHoursMode = searchPageParams.openingHoursMode || "any";
  const parsedOpeningHoursDay = searchPageParams.openingHoursDay || "";
  const parsedOpeningHoursTime = searchPageParams.openingHoursTime || "";

  // Search state for overlay interaction
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useAtom(searchOverlayOpenAtom);
  const [mapCenter] = useAtom(mapCenterAtom);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const routerState = useRouterState();
  const currentPathname = routerState.location.pathname;
  const panelFilterSections = searchPageSections(currentPathname, useSearchFilterSections());
  // Counted over the list the header search bar hands its panel, so badge and section counts agree.
  const displayedAccessibilityCount = useAccessibilitySelectionCount(
    parsedAccessibilityOptions,
    panelFilterSections ?? (filterAccessibility.length > 0 ? filterAccessibility : undefined),
  );
  const activeFilterCount = otherFilterCount + displayedAccessibilityCount;
  const hasActiveFilters = activeFilterCount > 0;
  const headerRef = useRef<HTMLElement>(null);
  
  // Fetch unread notifications count for badge
  const { data: unreadNotificationsData } = useNotifications("all", true);
  const unreadCount = useMemo(() => {
    if (
      !unreadNotificationsData?.pages ||
      unreadNotificationsData.pages.length === 0
    )
      return 0;
    return unreadNotificationsData.pages[0]?.pagination?.total || 0;
  }, [unreadNotificationsData]);

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "redirectToMySavePlace") {
        navigate({ to: "/mysavedplaces" });
        setActionToPerformAfterLogin("");
      }
    }
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  useEffect(() => {
    const timer = setTimeout(() => {
      console.log("isLoggedIn", isLoggedIn, user);
      if (showLoginByDefault === "Yes" && !isLoggedIn && !user) {
        showModal(AuthModalScreen.EnterEmail);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [showLoginByDefault, isLoggedIn, user]);

  const handleOpenAuthModal = () => {
    showModal(AuthModalScreen.EnterEmail);
  };

  const matchRoute = useMatchRoute();

  // Determine if the current route matches'/mysavedplaces'
  const isSavedActive =
    matchRoute({ to: "/mysavedplaces" }) ||
    matchRoute({ to: `/savedplacedetails/$savedPlaceId/user/$userId` });

  // Determine if the current route matches '/notifications'
  const isNotificationsActive = matchRoute({ to: "/notifications" });

  /**
   * "How to" opens the Learning Hub, as native's header does; home's
   * "Contribute info" has its own How To modal.
   */
  const openLearningHub = useOpenLearningHub();

  // CheckIcon if search box should be hidden for the current route
  const shouldHideSearch = ROUTES_WITHOUT_SEARCH.some((route) =>
    matchRoute({ to: route }),
  );

  // The results page searches in place: a live input with its filters below it,
  // no overlay. Desktop only; mobile keeps its own trigger and sheet.
  const isSearchResultsRoute = Boolean(matchRoute({ to: "/search" }));

  // Venue details wears the same header as the results page (design: the two
  // should not diverge), so it gets the in-place input and filter row too.
  const isVenueDetailsRoute = Boolean(
    matchRoute({ to: "/venuedetails/$venueId", fuzzy: true }),
  );

  // Drives the header's layout: which routes render the inline SearchBar with
  // its chip row instead of the collapsed "Open search" box.
  const usesInlineSearch = isSearchResultsRoute || isVenueDetailsRoute;

  // Fetch saved searches only when SearchBar is shown (not on routes where it's hidden)
  const { data: savedSearchesData } = useSavedSearches({
    enabled: !shouldHideSearch,
  });

  // Flatten all pages into a single array and calculate count
  const savedSearches = useMemo(() => {
    return savedSearchesData?.pages.flatMap((page) => page.searches) ?? [];
  }, [savedSearchesData?.pages]);

  const savedSearchesCount = savedSearches.length;

  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);

  const profileUrl = bucketUrlPrefix + (user as any)?.profile_photo;

  const [drawerMenuOpen, setDrawerMenuOpen] = useAtom(drawerMenuOpenAtom);

  const toggleDrawerMenu = () => {
    // Fire on the open transition only — both avatar triggers (logged-in avatar + logged-out
    // placeholder) route through here. Shared across pages, so use the trackEvent primitive.
    if (!drawerMenuOpen) {
      trackEvent("profile_menu_opened", { entry_page: entryPageFromCurrentPage() });
    }
    setIsNotificationPanelOpen(false);
    setDrawerMenuOpen((drawerMenuOpen) => !drawerMenuOpen);
  };

  const clearsetSelectedVenue = () => {
    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);
    setIsSearchOverlayOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

    // Centralized cleanup of all app state
    resetAllAppState({
      searchPageParams,
      setSearchPageParams,
      setViewMode,
      isMapper,
      setInternalSearchTerm,
    });

    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    navigate({ to: "/" });
  };

  const handleSavedClick = () => {
    if (!isLoggedIn || !user) {
      // Show login modal if user is not logged in
      showModal(AuthModalScreen.EnterEmail);
      return;
    }

    setSelectedVenue(null);
    setIsVenueDetailsOpen(false);
    setIsSearchOverlayOpen(false);

    setSavedPlaceIdToView("");
    setSavedPlaceByUserIdToView("");

     // Centralized cleanup of all app state
     resetAllAppState({
      searchPageParams,
      setSearchPageParams,
      setViewMode,
      isMapper,
      setInternalSearchTerm,
    });

    navigate({ to: "/mysavedplaces" });
  };

  const handleNotificationsClick = () => {
    if (!isLoggedIn || !user) {
      // Show login modal if user is not logged in
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    // Close search overlay before showing notification panel
    setIsSearchOverlayOpen(false);
    // Show notification panel if user is logged in
    // notification_nav_action_button -> notifications_viewed (the panel feed surface). Fire on the
    // open transition only; the panel opens on the "all" tab.
    if (!isNotificationPanelOpen) {
      trackEvent("notifications_viewed", {
        entry_page: entryPageFromCurrentPage(),
        entry_page_url: window.location.href,
        notification_type: "all",
        unread_notifications_count: unreadCount,
      });
    }
    setIsNotificationPanelOpen((prev) => !prev);
  };

  const handleNotificationPanelClose = () => {
    setIsNotificationPanelOpen(false);
  };

  useEffect(() => {
    setIsNotificationPanelOpen(false);
  }, [currentPathname]);

  // Pages size themselves with --navbar-height, which no longer holds on the
  // search page: its navbar is two rows tall. Publish what it actually measures.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const publish = () =>
      document.documentElement.style.setProperty(
        "--navbar-height",
        `${header.offsetHeight}px`,
      );

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(header);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--navbar-height");
    };
  }, []);

  // The in-place input reads the shared term atom, and nothing else fills it on
  // arrival. Only the committed term is echoed, so typing is left alone.
  useEffect(() => {
    if (isSearchResultsRoute) {
      setInternalSearchTerm(derivedSearchTerm);
    }
  }, [isSearchResultsRoute, derivedSearchTerm, setInternalSearchTerm]);

  const handleViewAllNotifications = () => {
    setIsNotificationPanelOpen(false);
    navigate({ to: "/notifications" });
  };

  const handleManageNotifications = () => {
    setIsNotificationPanelOpen(false);
    navigate({ to: "/settings" });
  };

  // Handle saved search selection
  const handleSavedSearchSelect = useHandleSavedSearchSelect({
    onSuccess: () => setIsSearchOverlayOpen(false),
  });

  const handleSearchBoxClick = () => {
    setIsNotificationPanelOpen(false);
    setIsSearchOverlayOpen(true);
  };

  const handleSearchOverlayClose = () => {
    setInternalSearchTerm(derivedSearchTerm);
    setIsSearchOverlayOpen(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearchSubmit = (term: string, count: number) => {
    // The search overlay updates global search params
    // Filter count is now derived from searchPageParams (term and count params kept for interface compatibility)
    setIsSearchOverlayOpen(false);
  };

  // Handle Escape key to close search overlay
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSearchOverlayOpen) {
        handleSearchOverlayClose();
      }
    };

    if (isSearchOverlayOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSearchOverlayOpen]);

  return (
    <>
    <header
      ref={headerRef}
      className="sticky top-0 z-[999] w-full bg-surface-secondary border-b border-border-token-brand-minimal"
    >
      <nav
        className={cn(
          "flex items-center justify-between gap-3 px-4 py-4 md:px-6 min-h-20 h-20",
          // The filter row makes this two rows tall on desktop; the logo and the
          // account links stay level with the input.
          usesInlineSearch && "lg:items-start lg:h-auto",
        )}
        aria-label="Main navigation"
      >
        {/* Logo Section */}
        <div
          className={cn(
            "flex flex-1 items-center",
            usesInlineSearch && "lg:h-12",
          )}
        >
          <button
            type="button"
            onClick={clearsetSelectedVenue}
            className="inline-flex p-0.5 rounded-md focus-blue"
            aria-label="Sociability Home"
          >
            {/* Full Logo - Hidden on very small screens. Both variants are always
                rendered and the `dark` class picks one, rather than branching on
                `useDarkMode()`: the server renders without `matchMedia`, and React
                does not patch a mismatched `src` when it hydrates, so the light
                wordmark stayed on screen in dark mode. */}
            <img
              className="h-10 w-auto hidden logo-small:hidden sm:block dark:hidden"
              src="/images/Logo.svg"
              alt=""
              aria-hidden="true"
            />
            <img
              className="h-10 w-auto hidden dark:sm:block"
              src="/images/Logo_dark.svg"
              alt=""
              aria-hidden="true"
            />
            {/* Icon Only Logo - Shown on very small screens */}
            <img
              className="h-10 w-10 block sm:hidden logo-small:block"
              src="/images/logo_icon.svg"
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Results page: search and filters live here, no overlay. Needs room for
            the chip row, so narrower screens keep the collapsed box and overlay. */}
        {usesInlineSearch && (
          <div className="hidden lg:block min-w-0 lg:flex-1 lg:max-w-[520px] xl:max-w-[560px]">
            <SearchBar
              layout="header"
              filtersBelowInput
              tagging_mode={searchPageParams.tagging_mode}
              isLoggedIn={isLoggedIn}
              searchTerm=""
              initialSearchTerm={derivedSearchTerm}
              selectedAccessibilityOptions={parsedAccessibilityOptions}
              selectedBusinessTypes={parsedBusinessTypes}
              openingHoursMode={parsedOpeningHoursMode}
              selectedDay={parsedOpeningHoursDay}
              selectedTime={parsedOpeningHoursTime}
              savedSearchesCount={savedSearchesCount}
              savedSearches={savedSearches}
              onSearchSubmit={() => {}}
              onSavedSearchesClick={() => {}}
              onSavedSearchSelect={handleSavedSearchSelect}
              latitude={mapCenter.lat || defaultRegion.latitude}
              longitude={mapCenter.lng || defaultRegion.longitude}
              accessibilityTagsData={
                filterAccessibility.length > 0 ? filterAccessibility : undefined
              }
              filterSectionsData={panelFilterSections}
            />
          </div>
        )}

        {/* Search Box - Clickable to open overlay, hidden when overlay is open or on specific routes */}
        {!isSearchOverlayOpen && !shouldHideSearch && (
          <button
            type="button"
            onClick={handleSearchBoxClick}
            className={cn(
              // Shrinks before the trailing controls do (design QA: "How to"
              // was wrapping onto two lines).
              "hidden md:flex flex-1 min-w-0 lg:max-w-[400px] items-center gap-4 h-[48px] px-4",
              usesInlineSearch && "lg:hidden",
              "bg-bg-brand-secondary-default",
              "border border-border-token-neutral-subtle rounded-lg",
              "transition-colors",
              "hover:border-primary-500 focus-blue cursor-pointer",
            )}
            aria-label={
              hasActiveFilters
                ? `${i18n.get("openSearch") || "Open search"}, ${i18n.t("sectionFiltersApplied", { count: activeFilterCount })}`
                : i18n.get("openSearch") || "Open search"
            }
          >
            {/* Search Icon */}
            <MagnifyingGlassIcon
              size={20}
              className="text-fg-neutral-subtle flex-shrink-0"
              strokeWidth={1.5}
              aria-hidden="true"
            />

            {/* Search Text / Placeholder */}
            <span
              className={cn(
                "flex-1 min-w-0 text-left truncate",
                "label-m-medium",
                internalSearchTerm
                  ? "text-fg-neutral-strong"
                  : "text-fg-neutral-subtle",
              )}
            >
              {internalSearchTerm
                ? internalSearchTerm
                : i18n.get("searchVenueOrLocation") ||
                  "Search venue or location"}
            </span>

            {/* Divider - Always visible */}
            <div
              className="h-6 w-px border border-border-token-neutral-minimal flex-shrink-0"
              aria-hidden="true"
            />

            {/* Filter Section - Always visible */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Filter Icon - Primary color when filters are active */}
              <SlidersHorizontalIcon
                size={20}
                className={cn(
                  hasActiveFilters
                    ? "text-fg-neutral-strong"
                    : "text-fg-neutral-subtle",
                )}
                strokeWidth={1.5}
                aria-hidden="true"
              />

              {/* Filter Count Badge - Show when filters are active */}
              {hasActiveFilters && (
                <span
                  className={cn(
                    "flex items-center justify-center min-w-[20px] h-5 rounded-full bg-container-brand-subtle text-fg-neutral-onFilled label-s-medium"
                  )}
                  aria-hidden="true"
                >
                  {activeFilterCount}
                </span>
              )}
            </div>
          </button>
        )}

        {/* Navigation Items Section */}
        <div
          className={cn(
            "flex flex-1 items-center justify-end gap-2 md:gap-4",
            usesInlineSearch && "lg:h-12",
          )}
        >
          {/* Notifications Button - Mobile Icon */}
          <button
            type="button"
            onClick={handleNotificationsClick}
            className="md:hidden inline-flex items-center justify-center gap-1 px-2 h-12 rounded-lg hover:bg-bg-brand-secondary-hover transition-colors focus-blue"
            aria-label={
              unreadCount > 0
                ? `${i18n.get("notifications") || "Notifications"}, ${unreadCount} unread`
                : i18n.get("notifications") || "View notifications"
            }
            aria-expanded={isNotificationPanelOpen}
            aria-haspopup="dialog"
          >
            <BellIcon size={20} className="text-fg-neutral-strong" aria-hidden="true" />
            {unreadCount > 0 && (
              <span
                className={`${
                  unreadCount > 99
                    ? "w-[24px] h-[24px]"
                    : unreadCount > 9
                      ? "w-[22px] h-[22px]"
                      : "w-4 h-4"
                } rounded-full flex justify-center items-center bg-[#8a0000]`}
                aria-hidden="true"
              >
                <span
                  className={`text-center text-neutral-50 ${
                    unreadCount > 99
                      ? "label-s-regular"
                      : unreadCount > 9
                        ? "label-s-regular"
                        : "label-s-medium"
                  }`}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </span>
            )}
          </button>

          {/* Notifications Button - Desktop */}
          <button
            type="button"
            onClick={handleNotificationsClick}
            className={`relative hidden md:inline-flex items-center justify-center min-h-[40px] min-w-[44px] px-3 py-2 rounded-lg label-m-medium transition-colors focus-blue ${
              isNotificationsActive
                ? "text-fg-brand-subtle bg-brand-ghost-active"
                : "text-fg-neutral-strong bg-brand-ghost-default hover:bg-brand-ghost-hover"
            }`}
            aria-label={
              unreadCount > 0
                ? `${i18n.get("notifications") || "Notifications"}, ${unreadCount} unread`
                : i18n.get("notifications") || "View notifications"
            }
            aria-expanded={isNotificationPanelOpen}
            aria-haspopup="dialog"
            aria-current={isNotificationsActive ? "page" : undefined}
          >
            <span aria-hidden="true">
              {i18n.get("notifications") || "Notifications"}
            </span>

            {unreadCount > 0 && (
              <span
                className="ml-2 h-5 min-w-5 px-1 rounded-full inline-flex justify-center items-center bg-[#8a0000]"
                aria-hidden="true"
              >
                <span className="text-center text-neutral-50 label-s-medium">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              </span>
            )}

            {isNotificationsActive && (
              <span
                className="absolute bottom-0 inset-x-2 h-0.5 rounded-full bg-[var(--focus-ring-default)]"
                aria-hidden="true"
              />
            )}
          </button>

          {/* Single Notification Panel - shared between mobile and desktop - only show when logged in */}
          {isLoggedIn && user && (
            <NotificationPanel
              isOpen={isNotificationPanelOpen}
              onClose={handleNotificationPanelClose}
              onViewAllClick={handleViewAllNotifications}
              onManageClick={handleManageNotifications}
              showBackdrop={true}
            />
          )}

          {/* Saved Button - Mobile Icon */}
          <button
            type="button"
            onClick={handleSavedClick}
            className={`md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg transition-colors focus-blue ${
              isSavedActive
                ? "bg-container-brand-minimal"
                : "hover:bg-bg-brand-secondary-hover"
            }`}
            aria-label={
              isSavedActive
                ? `${i18n.get("saved") || "Saved"} - current page`
                : i18n.get("saved") || "View saved places"
            }
            aria-current={isSavedActive ? "page" : undefined}
          >
            <HeartStraightIcon size={20} className="text-fg-neutral-strong" aria-hidden="true" />
          </button>

          {/* Saved Button - Desktop */}
          <button
            type="button"
            onClick={handleSavedClick}
            className={`relative hidden md:inline-flex items-center justify-center min-h-[40px] min-w-[44px] px-3 py-2 rounded-lg label-m-medium transition-colors focus-blue ${
              isSavedActive
                ? "text-fg-brand-subtle bg-brand-ghost-active"
                : "text-fg-neutral-strong bg-brand-ghost-default hover:bg-brand-ghost-hover"
            }`}
            aria-label={
              isSavedActive
                ? `${i18n.get("saved") || "Saved"} - current page`
                : i18n.get("saved") || "View saved places"
            }
            aria-current={isSavedActive ? "page" : undefined}
          >
            <span aria-hidden="true">{i18n.get("saved") || "Saved"}</span>

            {isSavedActive && (
              <span
                className="absolute bottom-0 inset-x-2 h-0.5 rounded-full bg-[var(--focus-ring-default)]"
                aria-hidden="true"
              />
            )}
          </button>

          {/* "How to" - opens the Learning Hub, and the third coach mark
              points here. Kept out of the signed-out/ signed-in fork below
              because it is the same control either way. */}
          <button
            type="button"
            data-tour-anchor="how-to"
            onClick={() => openLearningHub(undefined, "home_header")}
            // A secondary pill on white (Figma `15969:123982`), as native's
            // header draws it. `shrink-0 whitespace-nowrap`: the search box
            // gives way first, so the label never wraps under "How".
            className="hidden md:inline-flex shrink-0 items-center gap-2 h-10 px-3 rounded-full whitespace-nowrap
              border border-border-token-brand-minimal bg-container-neutral-white
              shadow-[0px_1px_2px_0px_rgba(7,7,8,0.08),0px_2px_6px_0px_rgba(7,7,8,0.12)]
              hover:bg-bg-brand-secondary-hover transition-colors focus-blue"
            aria-label={i18n.get("howToOpenLabel")}
          >
            <BookOpenTextIcon size={20} aria-hidden="true" className="text-fg-neutral-strong" />
            <span aria-hidden="true" className="label-m-medium text-fg-neutral-strong">
              {i18n.get("howTo")}
            </span>
          </button>

          {/* Profile Avatar Button */}
          {!user?.name ? (
            <>
              {/* Login Button - Desktop only */}
              <button
                type="button"
                onClick={handleOpenAuthModal}
                className="hidden md:inline-flex shrink-0 items-center justify-center h-10 px-6 rounded-lg whitespace-nowrap bg-bg-brand-primary-default hover:bg-bg-brand-primary-hover transition-colors focus-blue"
                aria-label={i18n.get("signIn")}
              >
                {/* "Sign in", as native's header says it. */}
                <span aria-hidden="true" className="label-m-medium text-white">
                  {i18n.get("signIn")}
                </span>
              </button>

              {/* Profile Placeholder */}
              <button
                type="button"
                onClick={toggleDrawerMenu}
                className="flex-shrink-0 w-10 h-10 rounded-full border border-border-token-brand-minimal overflow-hidden focus-blue"
                aria-label="Open profile menu"
              >
                <img
                  className="w-full h-full object-cover bg-neutral-300"
                  src="/images/user_placeholder.svg"
                  alt="Default profile icon"
                  aria-hidden="true"
                />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={toggleDrawerMenu}
              className="flex-shrink-0 w-10 h-10 rounded-full border border-border-token-brand-minimal overflow-hidden focus-blue"
              aria-label={`Open profile menu${user?.name ? ` for ${user.name}` : ""}`}
            >
              {(user as any)?.profile_photo ? (
                <img
                  className="w-full h-full object-cover"
                  src={profileUrl}
                  alt=""
                  aria-hidden="true"
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="flex items-center justify-center w-full h-full rounded-full text-white text-base font-medium select-none"
                  style={{
                    backgroundColor: user?.profile_color_code ?? "#8751FF",
                  }}
                >
                  {(() => { console.log("[Navbar] rendering initials avatar for user:", user?.name); return null; })()}
                  {user?.name?.charAt(0)?.toUpperCase() ?? ""}
                </div>
              )}
            </button>
          )}
        </div>
      </nav>

      {/* Hamburger Menu Drawer */}
      <div className="relative z-[1020]">
        <HamburgerMenu />
      </div>

      {/* Search Overlay */}
      <SearchBar
        layout="overlay"
        tagging_mode={searchPageParams.tagging_mode}
        isLoggedIn={isLoggedIn}
        isOverlayOpen={isSearchOverlayOpen}
        onOverlayClose={handleSearchOverlayClose}
        onOverlaySearch={handleSearchSubmit}
        initialSearchTerm={derivedSearchTerm}
        selectedAccessibilityOptions={parsedAccessibilityOptions}
        selectedBusinessTypes={parsedBusinessTypes}
        openingHoursMode={parsedOpeningHoursMode}
        selectedDay={parsedOpeningHoursDay}
        selectedTime={parsedOpeningHoursTime}
        searchTerm=""
        savedSearchesCount={savedSearchesCount}
        savedSearches={savedSearches}
        onSearchSubmit={() => {}}
        onSavedSearchesClick={() => {}}
        onSavedSearchSelect={handleSavedSearchSelect}
        latitude={mapCenter.lat || defaultRegion.latitude}
        longitude={mapCenter.lng || defaultRegion.longitude}
        locationLatitude={
          !isNaN(parseFloat(searchPageParams.userlatitude || "")) && isFinite(parseFloat(searchPageParams.userlatitude || ""))
          ? parseFloat(searchPageParams.userlatitude || "") 
          : undefined
        }
        locationLongitude={
          !isNaN(parseFloat(searchPageParams.userlongitude || "")) && isFinite(parseFloat(searchPageParams.userlongitude || ""))
          ? parseFloat(searchPageParams.userlongitude || "") 
          : undefined
        }
        accessibilityTagsData={filterAccessibility.length > 0 ? filterAccessibility : undefined}
        filterSectionsData={panelFilterSections}
      />
    </header>

    {/* Beside the header, not in it: the modal is not portalled, and inside
        the sticky header's z-[999] context it would stack under the page. */}
    <LearningHubModal />
    </>
  );
}

export default Navbar;
